import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Profile, UserRole } from '../types/database'
import { Sidebar } from './Sidebar'
import { CreateUserModal } from './CreateUserModal'
import { InviteUserModal } from './InviteUserModal'
import { QRCodeModal } from './QRCodeModal'

interface UserWithProfile extends Profile {
  user_roles?: { role: UserRole }[]
  created_at?: string
}

export function UserManagement() {
  const [users, setUsers] = useState<UserWithProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedUserForQR, setSelectedUserForQR] = useState<UserWithProfile | null>(null)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError('')

      const { data: users, error } = await supabase
        .from('profiles')
        .select(`*,
          user_roles(role)`)
      if (error) throw error

      setUsers(users || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : '사용자 목록을 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const getUserName = (user: UserWithProfile) => {
    const firstName = user.first_name || ''
    const lastName = user.last_name || ''
    return [lastName, firstName].filter(Boolean).join(' ') || 'Unknown User'
  }

  const filteredUsers = users.filter((u) => {
    const name = getUserName(u).toLowerCase()
    return name.includes(searchQuery.toLowerCase())
  })

  const getRoleBadgeColor = (role?: UserRole) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-700'
      case 'moderator':
        return 'bg-indigo-100 text-indigo-700'
      case 'user':
        return 'bg-blue-100 text-blue-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto">
          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
                사용자 관리
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                사용자의 역할을 관리합니다
              </p>
            </div>
          </header>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          )}

          <div className="mb-6 flex items-center gap-4">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-10 bg-white border border-gray-200">
              <div className="text-gray-400 flex items-center justify-center pl-3">
                <span className="material-symbols-outlined">search</span>
              </div>
              <input
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 pl-2 text-sm"
                placeholder="이름으로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 h-10 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">mail</span>
              <span className="text-sm font-medium">사용자 초대</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 h-10 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">person_add</span>
              <span className="text-sm font-medium">학생 계정 생성</span>
            </button>
            <button
              onClick={fetchUsers}
              className="flex items-center gap-2 h-10 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">refresh</span>
              <span className="text-sm font-medium">새로고침</span>
            </button>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">
                  people
                </span>
                <p className="text-gray-500">
                  {searchQuery ? '검색 결과가 없습니다.' : '사용자가 없습니다.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        이름
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        이메일
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        역할
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        관리
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="h-[72px] px-4 py-2 text-sm font-medium text-gray-800">
                          {getUserName(user)}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                          {user.email || '이메일 없음'}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          <div className="flex gap-2 flex-wrap">
                            {(user.user_roles && user.user_roles.length > 0) ? (
                              user.user_roles.map((ur, index) => (
                                <span
                                  key={index}
                                  className={`text-xs font-medium px-3 py-1.5 rounded-full ${getRoleBadgeColor(ur.role)}`}
                                >
                                  {ur.role}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-900 text-gray-100">
                                👻 Guest
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="h-[72px] px-4 py-2 text-right">
                          {user.plain_password && (
                            <button
                              onClick={() => setSelectedUserForQR(user)}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                              title="로그인 QR 코드 보기"
                            >
                              <span className="material-symbols-outlined text-xl">qr_code_2</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined text-blue-600 text-base">info</span>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">역할 안내</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>
                    <strong>Admin:</strong> 모든 기능 접근 가능 (사용자 관리 포함)
                  </li>
                  <li>
                    <strong>Moderator:</strong> 콘텐츠 관리 가능 (사용자 관리 제외)
                  </li>
                  <li>
                    <strong>User:</strong> 기본 생성 및 수정 권한
                  </li>
                  <li>
                    <strong>Guest:</strong> 읽기 전용 (역할 없음)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false)
            fetchUsers()
          }}
        />
      )}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            fetchUsers()
          }}
        />
      )}
      {selectedUserForQR && (
        <QRCodeModal
          email={selectedUserForQR.email}
          userName={getUserName(selectedUserForQR)}
          initialPassword={selectedUserForQR.plain_password}
          onClose={() => setSelectedUserForQR(null)}
        />
      )}
    </div>
  )
}
